import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryGroups = [
  { label: "Footwear", items: categories.slice(0, 1) },
  { label: "Clothing", items: categories.slice(1, 7) },
  { label: "Essentials", items: categories.slice(7, 10) },
  { label: "Lifestyle", items: categories.slice(10) },
];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isArticlePage = location.pathname.startsWith("/article");

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 80);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isHeroArea = !scrolled && !isArticlePage;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || isArticlePage
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          className={`flex items-center gap-2 cursor-pointer ${
            isHeroArea ? "text-background-50" : "text-foreground-900"
          }`}
        >
          <span className="text-lg tracking-tight font-heading">
            PANDABUY<span className={isHeroArea ? "text-primary-200" : "text-primary-500"}>.CASA</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <Link
            to="/"
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
              isHeroArea
                ? "text-background-50/85 hover:text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            Home
          </Link>

          <Link
            to="/article/shopping-guide"
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
              isHeroArea
                ? "text-background-50/85 hover:text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            Shopping Guide
          </Link>

          <Link
            to="/article/pandabuyspreadsheet-guide"
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
              isHeroArea
                ? "text-background-50/85 hover:text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            Blog
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                isHeroArea
                  ? "text-background-50/85 hover:text-background-50"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Categories
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 right-0 w-64 bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden shadow-lg z-50">
                <div className="p-2">
                  {categoryGroups.map((group) => (
                    <div key={group.label} className="mb-1 last:mb-0">
                      <div className="px-3 py-1.5 text-[10px] tracking-widest uppercase text-foreground-400 font-label">
                        {group.label}
                      </div>
                      {group.items.map((cat) => (
                        <a
                          key={cat.slug}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="w-7 h-7 rounded-md bg-background-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            <img src={cat.image} alt="" className="w-full h-full object-cover" />
                          </div>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm px-5 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              isHeroArea
                ? "bg-background-50/15 text-background-50 border border-background-50/30 hover:bg-background-50/25"
                : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            Pandabuy Sheet
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer ${
            isHeroArea
              ? "text-background-50 hover:bg-background-50/10"
              : "text-foreground-700 hover:bg-background-100"
          }`}
        >
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 py-3 px-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-line text-lg w-5 flex items-center justify-center"></i>
              Home
            </Link>
            <Link
              to="/article/shopping-guide"
              className="flex items-center gap-3 py-3 px-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line text-lg w-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>
            <Link
              to="/article/pandabuyspreadsheet-guide"
              className="flex items-center gap-3 py-3 px-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-article-line text-lg w-5 flex items-center justify-center"></i>
              Blog
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between py-3 px-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer mt-1"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg w-5 flex items-center justify-center"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-3 border-l-2 border-background-200 pl-3 space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 py-2.5 px-2 rounded-lg text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-7 h-7 rounded-md bg-background-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src={cat.image} alt="" className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 py-3 text-sm text-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pandabuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}